import { DestroyRef, inject, signal, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, fromEvent } from 'rxjs';
import {
  AccountStatus,
  AccountSortField,
  Account,
  AccountFilters,
} from '@olv/ui-shared/models/account.interface';
import { SearchState } from '@olv/ui-shared/ui-shared/pill-search/pill-search.component';

export abstract class AccountFilterBase {
  abstract readonly accounts: Signal<Account[]>;

  readonly sortOptions = [
    { label: 'Name (A-Z)', value: AccountSortField.NAME_ASC },
    { label: 'Name (Z-A)', value: AccountSortField.NAME_DESC },
  ];

  readonly statusOptions = [
    { label: 'Active', value: AccountStatus.ACTIVE },
    { label: 'Inactive', value: AccountStatus.INACTIVE },
  ];

  readonly DEFAULT_STATUS: AccountStatus = AccountStatus.ACTIVE;

  roleOptions: { label: string; value?: number }[] = [];
  selectedSortOption = 'Sort By';
  selectedStatusOption = 'Status';
  selectedRoleOption = 'Role';
  searchTerm = '';
  currentPage = 0;
  openDropdown: string | null = null;

  private static readonly NARROW_BREAKPOINT = 900;
  private static readonly NARROW_VIEWPORT_RATIO = 0.7;
  private static readonly MIN_LIST_HEIGHT = 320;

  private readonly viewport = signal({ width: window.innerWidth, height: window.innerHeight });

  constructor() {
    fromEvent(window, 'resize')
      .pipe(debounceTime(150), takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() => this.viewport.set({ width: window.innerWidth, height: window.innerHeight }));
  }

  // Keeps the list from becoming a short scroll window inside a scrolling page on phones
  listViewportHeight(desktopHeight: string | number): string | number {
    const { width, height } = this.viewport();
    if (width > AccountFilterBase.NARROW_BREAKPOINT) {
      return desktopHeight;
    }
    return Math.max(
      AccountFilterBase.MIN_LIST_HEIGHT,
      Math.round(height * AccountFilterBase.NARROW_VIEWPORT_RATIO),
    );
  }

  get sortOptionsLabels(): string[] {
    return this.sortOptions.map((o) => o.label);
  }

  get statusOptionsLabels(): string[] {
    return this.statusOptions.map((o) => o.label);
  }

  get roleOptionsLabels(): string[] {
    return this.roleOptions.map((o) => o.label);
  }

  get filteredAccounts(): Account[] {
    return this.accounts();
  }

  onOptionSelected(event: { id: string; value: string }): void {
    switch (event.id) {
      case 'sort': {
        const sortOption = this.sortOptions.find((o) => o.label === event.value);
        if (sortOption) {
          this.onSortSelect(sortOption);
        }
        break;
      }
      case 'status': {
        const statusOption = this.statusOptions.find((o) => o.label === event.value);
        if (statusOption) {
          this.onStatusSelect(statusOption);
        }
        break;
      }
      case 'role': {
        const roleOption = this.roleOptions.find((o) => o.label === event.value);
        if (roleOption) {
          this.onRoleSelect(roleOption);
        }
        break;
      }
    }
    this.openDropdown = null;
  }

  onCleared(e: { id: string }): void {
    switch (e.id) {
      case 'sort':
        this.selectedSortOption = 'Sort By';
        this.updateFilters({ sortBy: undefined });
        break;

      case 'status':
        this.selectedStatusOption = 'Status';
        this.updateFilters({ status: undefined });
        break;

      case 'role':
        this.selectedRoleOption = 'Role';
        this.updateFilters({ role: undefined });
        break;
    }
  }

  onSortSelect(option: { label: string; value: AccountSortField }): void {
    this.selectedSortOption = option.label;
    this.updateFilters({ sortBy: option.value });
  }

  onStatusSelect(option: { label: string; value?: AccountStatus }): void {
    this.selectedStatusOption = option.label;
    this.updateFilters({ status: option.value });
  }

  onRoleSelect(option: { label: string; value?: number }): void {
    this.selectedRoleOption = option.label;
    this.updateFilters({ role: option.value });
  }

  onSearchChange(searchState: SearchState): void {
    this.searchTerm = searchState.value;
    this.updateFilters({ search: searchState.value });
  }

  protected abstract updateFilters(newFilters: Partial<AccountFilters>): void;
}
